import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { FileText, Landmark, GitCompareArrows, ReceiptText, BarChart3 } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import Facturas from "../../components/os/contab/Facturas";
import Bancos from "../../components/os/contab/Bancos";
import Conciliacion from "../../components/os/contab/Conciliacion";
import Tickets from "../../components/os/contab/Tickets";
import Reportes from "../../components/os/contab/Reportes";

const ease = [0.16, 1, 0.3, 1];
const TABS = [
  { key: "facturas", icon: FileText },
  { key: "bancos", icon: Landmark },
  { key: "conciliacion", icon: GitCompareArrows },
  { key: "tickets", icon: ReceiptText },
  { key: "reportes", icon: BarChart3 },
];

export default function Contabilidad() {
  const { t } = useI18n();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [years, setYears] = useState([new Date().getFullYear()]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(0);
  const [tab, setTab] = useState("facturas");

  useEffect(() => {
    api.get("/accounting/companies").then((r) => {
      setCompanies(r.data);
      if (r.data.length) setCompanyId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const loadYears = useCallback(() => {
    if (!companyId) return;
    api.get(`/accounting/years?company_id=${companyId}`).then((r) => {
      const ys = r.data.length ? r.data : [new Date().getFullYear()];
      setYears(ys);
      setYear((y) => (ys.includes(y) ? y : ys[0]));
    }).catch(() => {});
  }, [companyId]);
  useEffect(() => { loadYears(); }, [loadYears]);

  const shared = { companyId, year, month, onDataChange: loadYears };

  return (
    <OSLayout title={t("os.contab.wrapperTitle")}>
      <div className="space-y-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2">{t("os.contab.wrapperSubtitle")}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.contab.wrapperTitle")}</h2>
          <p className="mt-2 text-navy/50">{t("os.contab.wrapperIntro")}</p>
        </motion.div>

        {/* Company switcher */}
        <div className="flex flex-wrap items-center gap-2" data-testid="acct-company-switcher">
          {companies.map((c) => (
            <button key={c.id} data-testid={`acct-company-${c.id}`} onClick={() => setCompanyId(c.id)}
              className={`text-left rounded-xl border px-4 py-3 transition-all ${companyId === c.id ? "border-violet bg-white shadow-sm" : "border-navy/10 bg-white/50 hover:border-navy/20"}`}>
              <div className="text-sm font-medium">{c.name}</div>
              <div className="font-mono text-[11px] text-navy/40">{c.rfc}</div>
            </button>
          ))}
        </div>

        {/* Period filters */}
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="acct-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet">
            <option value={0}>{t("os.contab.allYears")}</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select data-testid="acct-month" value={month} onChange={(e) => setMonth(Number(e.target.value))}
            className="bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet">
            {t("os.contab.months").map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-navy/10 overflow-x-auto no-scrollbar" data-testid="acct-tabs">
          {TABS.map((tb) => (
            <button key={tb.key} data-testid={`acct-tab-${tb.key}`} onClick={() => setTab(tb.key)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === tb.key ? "border-violet text-violet font-medium" : "border-transparent text-navy/50 hover:text-navy"}`}>
              <tb.icon className="w-4 h-4" /> {t(`os.contab.tabs.${tb.key}`)}
            </button>
          ))}
        </div>

        <div className="pt-2">
          {!companyId ? <div className="text-navy/40">{t("os.contab.loading")}</div> : (
            <>
              {tab === "facturas" && <Facturas {...shared} />}
              {tab === "bancos" && <Bancos {...shared} />}
              {tab === "conciliacion" && <Conciliacion {...shared} />}
              {tab === "tickets" && <Tickets {...shared} />}
              {tab === "reportes" && <Reportes {...shared} />}
            </>
          )}
        </div>
      </div>
    </OSLayout>
  );
}
